/** Independent evidence clocks and provider state. Never infer observation time from retrieval. */
export type InformationTime =
  | { precision: "instant"; value: string }
  | { precision: "date"; value: string }
  | { precision: "local"; value: string; timeZone: string | null };

export type FailureCode = "missing-configuration" | "authentication" | "entitlement" | "rate-limit"
  | "local-budget-cutover" | "local-budget-exhausted" | "local-budget-unavailable"
  | "http" | "malformed-response" | "timeout" | "network" | "unknown";

const FAILURE_MESSAGES: Record<FailureCode, string> = {
  "missing-configuration": "Provider configuration is missing",
  authentication: "Provider authentication was rejected",
  entitlement: "Provider access is not included in the available plan",
  "rate-limit": "Provider rate limit reached",
  "local-budget-cutover": "Requests paused while earlier usage leaves the 24-hour free allowance window",
  "local-budget-exhausted": "Local free allowance is exhausted; no provider request was sent",
  "local-budget-unavailable": "Local request budget is unavailable; no provider request was sent",
  http: "Provider HTTP request failed",
  "malformed-response": "Provider response was invalid",
  timeout: "Provider request timed out",
  network: "Provider network request failed",
  unknown: "Provider request failed for an unknown reason",
};

export interface ProviderFailure {
  code: FailureCode;
  occurredAt: string;
  httpStatus?: number;
  retryAt?: string;
  requestSent?: boolean;
  message: string;
}

export interface InformationClocks {
  observedAt: InformationTime | null;
  publishedAt: InformationTime | null;
  updatedAt: InformationTime | null;
  providerResponseAt: InformationTime | null;
  providerSeenAt: InformationTime | null;
  effectiveAt: InformationTime | null;
  retrievedAt: string | null;
  cacheInsertedAt: string | null;
  lastSuccessfulRetrievalAt: string | null;
  lastAttemptAt: string | null;
  nextExpectedReleaseAt: InformationTime | null;
  validUntil: string | null;
  cacheRetainUntil: string | null;
  vintage: string | null;
  vintageStart: InformationTime | null;
  vintageEnd: InformationTime | null;
}

export interface InformationPolicy {
  kind: "continuous" | "session" | "periodic" | "event" | "news" | "unknown";
  clock: "observedAt" | "publishedAt" | "updatedAt";
  currentWithinMs?: number;
  declaredDelayMs?: number;
}

export interface InformationSession {
  state: "open" | "closed" | "unknown" | "not-applicable";
  basis: "provider" | "regular-hours" | "continuous" | "unknown";
  timeZone: string | null;
  sessionDate: string | null;
  latestSessionStartAt: string | null;
  latestSessionEndAt: string | null;
  nextSessionStartAt: string | null;
  latestObservation: boolean;
}

export interface InformationBase {
  version: "tsm-information-v1";
  provider: {
    id: string;
    health: "healthy" | "degraded" | "failed" | "unknown";
    activity: "idle" | "refreshing" | "cooldown";
    failure: ProviderFailure | null;
  };
  data: {
    availability: "available" | "partial" | "empty" | "warming" | "unavailable";
    timeliness: "current" | "delayed" | "latest-published" | "closed-session" | "older-observation" | "unknown";
    validity: "valid" | "invalid" | "expired" | "unknown";
  };
  delivery: "provider" | "cache" | "last-known-good" | "local" | "none";
  clocks: InformationClocks;
  session: InformationSession;
  policy: InformationPolicy;
}

export interface InformationComponent extends InformationBase {
  id: string;
  instrument: string | null;
  venue: string | null;
  unit: string | null;
  currency: string | null;
  value?: number | string | null;
}

export interface InformationState extends InformationBase {
  components?: InformationComponent[];
}

export interface InformationOverrides {
  provider?: Partial<InformationBase["provider"]>;
  data?: Partial<InformationBase["data"]>;
  delivery?: InformationBase["delivery"];
  clocks?: Partial<InformationClocks>;
  session?: Partial<InformationSession>;
  policy?: Partial<InformationPolicy>;
  components?: InformationComponent[];
}

export function createInformationState(providerId: string, overrides: InformationOverrides = {}): InformationState {
  return {
    version: "tsm-information-v1",
    provider: { id: providerId, health: "unknown", activity: "idle", failure: null, ...overrides.provider },
    data: { availability: "unavailable", timeliness: "unknown", validity: "unknown", ...overrides.data },
    delivery: overrides.delivery ?? "none",
    clocks: {
      observedAt: null, publishedAt: null, updatedAt: null, providerResponseAt: null, providerSeenAt: null, effectiveAt: null,
      retrievedAt: null, cacheInsertedAt: null, lastSuccessfulRetrievalAt: null,
      lastAttemptAt: null, nextExpectedReleaseAt: null, validUntil: null,
      cacheRetainUntil: null, vintage: null, vintageStart: null, vintageEnd: null,
      ...overrides.clocks,
    },
    session: {
      state: "unknown", basis: "unknown", timeZone: null, sessionDate: null,
      latestSessionStartAt: null, latestSessionEndAt: null, nextSessionStartAt: null,
      latestObservation: false, ...overrides.session,
    },
    policy: { kind: "unknown", clock: "observedAt", ...overrides.policy },
    ...(overrides.components ? { components: overrides.components } : {}),
  };
}

function validCalendar(year: number, month: number, day: number) {
  if (year < 1 || year > 9999 || month < 1 || month > 12 || day < 1) return false;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  return day <= [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
}

/** Accept only precision and zones present in the provider value or its documented contract. */
export function parseInformationTime(
  value: unknown,
  options: { timeZone?: string; epochUnit?: "seconds" | "milliseconds" } = {},
): InformationTime | null {
  if (options.epochUnit) {
    if ((typeof value !== "number" && typeof value !== "string") || value === ""
      || (typeof value === "string" && !/^-?\d+(?:\.\d+)?$/.test(value))) return null;
    const epoch = Number(value) * (options.epochUnit === "seconds" ? 1000 : 1);
    if (!Number.isFinite(epoch) || !Number.isFinite(new Date(epoch).getTime())) return null;
    return { precision: "instant", value: new Date(epoch).toISOString() };
  }
  if (typeof value !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}):(\d{2})(\.\d{1,9})?(Z|[+-]\d{2}:\d{2})?)?$/.exec(value);
  if (!match || !validCalendar(Number(match[1]), Number(match[2]), Number(match[3]))) return null;
  if (match[4] === undefined) return { precision: "date", value };
  if (Number(match[4]) > 23 || Number(match[5]) > 59 || Number(match[6]) > 59) return null;
  let normalized = value.replace(" ", "T");
  if (!match[8]) {
    if (options.timeZone === "UTC" || options.timeZone === "GMT") normalized += "Z";
    else return { precision: "local", value: normalized, timeZone: options.timeZone ?? null };
  } else if (match[8] !== "Z") {
    const [hours, minutes] = match[8].slice(1).split(":").map(Number);
    if (hours > 23 || minutes > 59) return null;
  }
  const epoch = Date.parse(normalized);
  return Number.isFinite(epoch) ? { precision: "instant", value: new Date(epoch).toISOString() } : null;
}

function instantMs(value: unknown): number | null {
  const parsed = parseInformationTime(value);
  return parsed?.precision === "instant" ? Date.parse(parsed.value) : null;
}

export class ProviderRequestError extends Error {
  readonly code: FailureCode;
  readonly httpStatus?: number;
  readonly retryAt?: string;

  constructor(code: FailureCode, options: { httpStatus?: number; retryAt?: string } = {}) {
    super(FAILURE_MESSAGES[code]);
    this.name = "ProviderRequestError";
    this.code = code;
    this.httpStatus = options.httpStatus;
    this.retryAt = options.retryAt;
  }
}

export function classifyProviderFailure(
  error: unknown,
  context: { provider: string; now: string; explicitCode?: FailureCode },
): ProviderFailure {
  let code: FailureCode = context.explicitCode ?? "unknown";
  let httpStatus: number | undefined;
  let retryAt: string | undefined;
  if (error instanceof ProviderRequestError) {
    code = context.explicitCode ?? error.code;
    httpStatus = error.httpStatus;
    retryAt = error.retryAt;
  } else if (error instanceof Error) {
    const status = /^HTTP (\d{3})$/.exec(error.message);
    const candidate = (error as Error & { status?: unknown }).status;
    httpStatus = status ? Number(status[1]) : typeof candidate === "number" ? candidate : undefined;
    if (!context.explicitCode) {
      if (httpStatus !== undefined) code = httpStatus === 401 ? "authentication" : httpStatus === 429 ? "rate-limit" : "http";
      else if (error.name === "AbortError" || error.name === "TimeoutError") code = "timeout";
      else if (error instanceof SyntaxError) code = "malformed-response";
      else if (error instanceof TypeError && /^(fetch failed|failed to fetch|network request failed|load failed)$/i.test(error.message)) code = "network";
    }
  }
  if (code === "http" && !context.explicitCode) {
    if (httpStatus === 401) code = "authentication";
    if (httpStatus === 429) code = "rate-limit";
  }
  return {
    code, occurredAt: context.now, message: FAILURE_MESSAGES[code],
    ...(Number.isInteger(httpStatus) && httpStatus! >= 100 && httpStatus! <= 599 ? { httpStatus } : {}),
    ...(instantMs(retryAt) !== null ? { retryAt } : {}),
  };
}

function ownUsable(state: InformationBase) {
  return state.data.validity === "valid"
    && (state.data.availability === "available" || state.data.availability === "partial");
}

/** Pure reevaluation: a cache hit cannot extend a value's original validity or retention. */
export function evaluateInformation(state: InformationState, nowMs: number): InformationState {
  const result: InformationState = {
    ...state, provider: { ...state.provider }, data: { ...state.data },
    clocks: { ...state.clocks }, session: { ...state.session }, policy: { ...state.policy },
    ...(state.components ? { components: state.components.map((component) => ({
      ...component, ...evaluateInformation(component, nowMs),
    })) } : {}),
  };
  const semanticExpiry = instantMs(result.clocks.validUntil);
  const retentionExpiry = instantMs(result.clocks.cacheRetainUntil);
  if ((semanticExpiry !== null && nowMs >= semanticExpiry)
    || (retentionExpiry !== null && nowMs >= retentionExpiry)) {
    result.data.validity = "expired";
    result.data.availability = "unavailable";
    result.data.timeliness = "unknown";
    result.delivery = "none";
    return result;
  }
  if (result.components?.length) {
    const usableCount = result.components.filter(ownUsable).length;
    if (usableCount < result.components.length && ownUsable(result)) {
      result.data.availability = usableCount ? "partial" : "unavailable";
      if (!usableCount) result.data.validity = result.components.every((item) => item.data.validity === "expired") ? "expired" : "unknown";
    }
  }
  result.data.timeliness = "unknown";
  if (!ownUsable(result) || !Number.isFinite(nowMs)) return result;
  const clock = result.clocks[result.policy.clock];
  if (!clock || result.policy.kind === "unknown") return result;
  if (result.policy.kind === "periodic") {
    const valid = parseInformationTime(clock.value, clock.precision === "local" ? { timeZone: clock.timeZone ?? undefined } : {});
    // A date is not an instant. Allow the latest possible local calendar day
    // (UTC+14) without inventing midnight or accepting a clearly future period.
    const latestPossibleDate = new Date(nowMs + 14 * 60 * 60 * 1000).toISOString().slice(0, 10);
    if (valid && (valid.precision === "instant"
      ? Date.parse(valid.value) <= nowMs
      : valid.value.slice(0, 10) <= latestPossibleDate)) result.data.timeliness = "latest-published";
    return result;
  }
  if (clock.precision !== "instant") return result;
  const time = instantMs(clock.value);
  if (time === null || time > nowMs) return result;
  const session = result.session;
  const sessionStart = instantMs(session.latestSessionStartAt);
  const sessionEnd = instantMs(session.latestSessionEndAt);
  const nextSession = instantMs(session.nextSessionStartAt);
  if (result.policy.kind === "session" && session.state === "closed" && session.latestObservation
    && (session.basis === "provider" || session.basis === "regular-hours")
    && sessionStart !== null && sessionEnd !== null && nextSession !== null
    && sessionStart <= time && time <= sessionEnd && sessionEnd <= nowMs && nowMs < nextSession) {
    result.data.timeliness = "closed-session";
    return result;
  }
  // An age threshold cannot override missing or outdated session evidence.
  if (result.policy.kind === "session" && (session.state !== "open" || session.basis !== "provider")) return result;
  const currentWindow = result.policy.currentWithinMs;
  const delay = result.policy.declaredDelayMs;
  const declaredDelay = typeof delay === "number" && Number.isFinite(delay) && delay > 0 ? delay : 0;
  if (typeof currentWindow === "number" && Number.isFinite(currentWindow) && currentWindow >= 0) {
    result.data.timeliness = nowMs - time <= currentWindow + declaredDelay
      ? declaredDelay > 0 ? "delayed" : "current"
      : "older-observation";
  } else if (declaredDelay > 0) result.data.timeliness = "delayed";
  else if (result.policy.kind === "event" && semanticExpiry !== null && semanticExpiry > nowMs) result.data.timeliness = "current";
  return result;
}

export function applyCachedDelivery(
  original: InformationState,
  input: { nowMs: number; refreshFailure?: ProviderFailure; refreshing?: boolean; cooldown?: boolean },
): InformationState {
  const failure = input.refreshFailure ?? original.provider.failure;
  const localDeferral = failure?.code.startsWith("local-budget-") ?? false;
  const noRequest = localDeferral || failure?.requestSent === false;
  const result: InformationState = {
    ...original,
    delivery: original.delivery === "local" ? "local" : original.delivery === "last-known-good" ? "last-known-good"
      : failure && (ownUsable(original) || original.data.availability === "empty") ? "last-known-good"
        : original.delivery === "none" ? "none" : "cache",
    provider: {
      ...original.provider,
      ...(failure ? { health: noRequest || (!input.refreshFailure && original.provider.health === "unknown") ? "unknown" as const : "failed" as const, failure } : {}),
      activity: localDeferral || input.cooldown ? "cooldown" : input.refreshing ? "refreshing" : "idle",
    },
    clocks: {
      ...original.clocks,
      ...(input.refreshFailure && !noRequest ? { lastAttemptAt: input.refreshFailure.occurredAt } : {}),
    },
    ...(original.components ? { components: original.components.map((component) => ({
      ...component, ...applyCachedDelivery(component, input),
    })) } : {}),
  };
  return evaluateInformation(result, input.nowMs);
}

export function isUsableInformation(state: InformationState, nowMs = Date.now()): boolean {
  const current = evaluateInformation(state, nowMs);
  return ownUsable(current)
    && (!current.components?.length || current.components.some(ownUsable));
}

export function isAlertEligibleInformation(state: InformationState, nowMs = Date.now()): boolean {
  const current = evaluateInformation(state, nowMs);
  const success = instantMs(current.clocks.lastSuccessfulRetrievalAt);
  return current.data.validity === "valid" && current.data.availability === "available"
    && current.data.timeliness === "current" && current.provider.health === "healthy"
    && current.provider.activity === "idle" && current.provider.failure === null
    && (current.delivery === "provider" || current.delivery === "cache")
    && success !== null && success <= nowMs
    && (!current.components?.length || current.components.every((component) => isAlertEligibleInformation(component, nowMs)));
}

export function informationLabel(state: InformationBase): string {
  if (state.data.validity === "expired") return "Previous data expired";
  if (state.data.availability === "warming") return "Warming";
  if (state.data.validity === "invalid") return "Invalid provider response";
  const localCode = state.provider.failure?.code;
  if (localCode?.startsWith("local-budget-")) {
    const retained = state.delivery === "last-known-good" ? "Cached last known good · " : "";
    return retained + (localCode === "local-budget-cutover" ? "Free allowance startup cooldown"
      : localCode === "local-budget-exhausted" ? "Local free allowance exhausted" : "Local budget unavailable");
  }
  if (state.data.availability === "unavailable") return state.provider.failure?.code === "missing-configuration"
    ? "Configuration needed" : state.provider.activity === "cooldown" ? "Provider unavailable · retry cooldown" : "Provider unavailable";
  if (state.data.validity === "unknown") return "Data validity unknown";
  const failureSuffix = state.provider.failure ? state.provider.failure.requestSent === false ? " · request not sent" : " · refresh failed" : "";
  if (state.delivery === "last-known-good") return "Cached last known good" + failureSuffix;
  if (state.data.availability === "empty") return state.provider.health === "healthy" && !state.provider.failure
    ? "No qualifying results" : "Provider unavailable";
  const currentLabel = state.policy.clock === "publishedAt" ? "Current publication"
    : state.policy.clock === "updatedAt" ? "Current provider update" : "Current observation";
  const labels: Record<InformationBase["data"]["timeliness"], string> = {
    current: currentLabel, delayed: "Delayed observation", "latest-published": "Latest published observation",
    "closed-session": "Latest closed-session observation", "older-observation": "Older observation", unknown: "Observation time state unknown",
  };
  const suffix = state.delivery === "cache" ? " · cached" : state.delivery === "local" ? " · local source" : "";
  return labels[state.data.timeliness] + (state.data.availability === "partial" ? " · partial" : "") + suffix
    + failureSuffix;
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}
function oneOf<T extends string>(value: unknown, values: readonly T[]): value is T {
  return typeof value === "string" && values.includes(value as T);
}
function nullableText(value: unknown, maximum = 160): string | null {
  return typeof value === "string" && value.length <= maximum && !/[\u0000-\u001f]/.test(value) ? value : null;
}

/** Allowlisted durable projection: discard unknown fields and never trust stored failure prose. */
export function sanitizeInformationState(value: unknown): InformationState | null {
  return sanitizeState(value, true);
}

function sanitizeState(value: unknown, allowComponents: boolean): InformationState | null {
  const root = record(value);
  const provider = record(root?.provider);
  const data = record(root?.data);
  const clocks = record(root?.clocks);
  const policy = record(root?.policy);
  const session = record(root?.session);
  if (root?.version !== "tsm-information-v1" || !provider || !data || !clocks || !policy || !session
    || !nullableText(provider.id) || !oneOf(provider.health, ["healthy", "degraded", "failed", "unknown"])
    || !oneOf(provider.activity, ["idle", "refreshing", "cooldown"])
    || !oneOf(data.availability, ["available", "partial", "empty", "warming", "unavailable"])
    || !oneOf(data.timeliness, ["current", "delayed", "latest-published", "closed-session", "older-observation", "unknown"])
    || !oneOf(data.validity, ["valid", "invalid", "expired", "unknown"])
    || !oneOf(root.delivery, ["provider", "cache", "last-known-good", "local", "none"])
    || !oneOf(policy.kind, ["continuous", "session", "periodic", "event", "news", "unknown"])
    || !oneOf(policy.clock, ["observedAt", "publishedAt", "updatedAt"])
    || !oneOf(session.state, ["open", "closed", "unknown", "not-applicable"])
    || !oneOf(session.basis, ["provider", "regular-hours", "continuous", "unknown"])) return null;
  const safe = createInformationState(provider.id as string, {
    provider: { health: provider.health, activity: provider.activity },
    data: { availability: data.availability, timeliness: data.timeliness, validity: data.validity },
    delivery: root.delivery, policy: { kind: policy.kind, clock: policy.clock },
    session: { state: session.state, basis: session.basis, timeZone: nullableText(session.timeZone),
      sessionDate: nullableText(session.sessionDate), latestObservation: session.latestObservation === true },
  });
  for (const key of ["currentWithinMs", "declaredDelayMs"] as const) {
    const input = policy[key];
    if (typeof input === "number" && Number.isFinite(input) && input >= 0) safe.policy[key] = input;
  }
  for (const key of ["observedAt", "publishedAt", "updatedAt", "providerResponseAt", "providerSeenAt", "effectiveAt", "nextExpectedReleaseAt", "vintageStart", "vintageEnd"] as const) {
    const clock = record(clocks[key]);
    if (!clock || !oneOf(clock.precision, ["instant", "date", "local"])) continue;
    const parsed = parseInformationTime(clock.value, clock.precision === "local" ? { timeZone: nullableText(clock.timeZone) ?? undefined } : {});
    if (parsed?.precision === clock.precision) safe.clocks[key] = parsed;
  }
  for (const key of ["retrievedAt", "cacheInsertedAt", "lastSuccessfulRetrievalAt", "lastAttemptAt", "validUntil", "cacheRetainUntil"] as const) {
    const parsed = parseInformationTime(clocks[key]);
    // Reject an invalid asserted expiry; silently deleting it could admit expired evidence.
    if ((key === "validUntil" || key === "cacheRetainUntil") && clocks[key] != null && parsed?.precision !== "instant") return null;
    safe.clocks[key] = parsed?.precision === "instant" ? parsed.value : null;
  }
  safe.clocks.vintage = nullableText(clocks.vintage);
  for (const key of ["latestSessionStartAt", "latestSessionEndAt", "nextSessionStartAt"] as const) {
    const parsed = parseInformationTime(session[key]);
    safe.session[key] = parsed?.precision === "instant" ? parsed.value : null;
  }
  if (provider.failure !== null && provider.failure !== undefined) {
    const failure = record(provider.failure);
    const at = parseInformationTime(failure?.occurredAt);
    if (!failure || !oneOf(failure.code, Object.keys(FAILURE_MESSAGES) as FailureCode[]) || at?.precision !== "instant") return null;
    safe.provider.failure = classifyProviderFailure(new ProviderRequestError(failure.code, {
      httpStatus: typeof failure.httpStatus === "number" ? failure.httpStatus : undefined,
      retryAt: typeof failure.retryAt === "string" ? failure.retryAt : undefined,
    }), { provider: safe.provider.id, now: at.value, explicitCode: failure.code });
    if (typeof failure.requestSent === "boolean") safe.provider.failure.requestSent = failure.requestSent;
  }
  if (allowComponents && root.components !== undefined) {
    if (!Array.isArray(root.components) || root.components.length > 64) return null;
    const components: InformationComponent[] = [];
    for (const item of root.components) {
      const component = record(item);
      const base = sanitizeState(item, false);
      if (!component || !base || !nullableText(component.id)) return null;
      const scalar = component.value;
      components.push({
        ...base, id: component.id as string, instrument: nullableText(component.instrument),
        venue: nullableText(component.venue), unit: nullableText(component.unit), currency: nullableText(component.currency),
        ...(scalar === null || typeof scalar === "number" && Number.isFinite(scalar) || typeof scalar === "string" && scalar.length <= 160
          ? { value: scalar as number | string | null } : {}),
      });
    }
    safe.components = components;
  }
  return safe;
}
